import { ApolloError, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { AlertInfo, AlertStatus } from 'common/graphqlTypes';
import { ALERTS_QUERY } from 'components/alerts/queries';
import { isTemplatedAlert } from 'components/alerts/alert';

type Alert = AlertInfo & {
  id: string;
  selected: boolean;
};

export type AlertModel = {
  alerts: Array<Alert>;
  setAlerts: (alerts: Array<Alert>) => void;
  activeTab: string;
  setActiveTab: (eventKey: string) => void;
  handleSelectAlert: (id: string) => void;
  getSelectedAlerts: () => Array<Alert>;
  hasSelectedAllAlerts: boolean;
  hasSelectedAllTriggeredAlerts: boolean;
  hasSelectedAllMutedAlerts: boolean;
  hasSelectedAllIncompleteAlerts: boolean;
  hasSelectedAtLeastOneAlert: boolean;
  hasSelectedTemplatedAlerts: boolean;
  handleSelectAllTriggeredAlerts: () => void;
  handleSelectAllMutedAlerts: () => void;
  handleSelectAllIncompleteAlerts: () => void;
  handleSelectAllAlerts: () => void;
  unselectAlerts: () => void;
  mutedAlerts: Array<Alert>;
  triggeredAlerts: Array<Alert>;
  incompleteAlerts: Array<Alert>;
  isCurrentlyOnTriggeredTab: boolean;
  isCurrentlyOnMutedTab: boolean;
  isCurrentlyOnIncompleteTab: boolean;
  isCurrentlyOnAllTab: boolean;
  setSearchText: (searchText: string) => void;
  searchText: string;
};

type AlertHook = {
  alertModel: AlertModel;
  loading: boolean;
  error: ApolloError | undefined;
};

const TRIGGERED_ALERTS_TAB = 'triggered';
const MUTED_ALERTS_TAB = 'muted';
const INCOMPLETE_ALERTS_TAB = 'incomplete';
const ALL_ALERTS_TAB = 'all';

/**
 * This hook serves as a method manager that exposes several methods to modify alerts, particularly useful in Alerts page.
 * TODO: this hook re uses a lot of very similar logic for Muted, Triggered, and now Incomplete tabs/alerts.
 *       There is probably a way to consolodate this logic into common functions that take in an alert type as a parameter or something.
 *       When making Alert page improvements, this should be revisited.
 */

function useAlerts(): AlertHook {
  // Editing an alert from the alert details page does not persist the changes when you go back to the alerts page
  // For now, we are using a fetchPolicy of network-only to ensure we always have the correct data
  const { loading, error, data } = useQuery(ALERTS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [alerts, setAlerts] = useState<Array<Alert>>([]);
  const [activeTab, setActiveTab] = useState(TRIGGERED_ALERTS_TAB);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (data) {
      const newAlerts = data.alerts.map((alert: Alert) => ({
        ...alert,
        selected: false,
      }));
      setAlerts(newAlerts);
    }
  }, [data]);

  // Filter alerts by the search text. However, the alert descriptions is not guaranteed. In that case, we must filter
  // using the alert trigger.
  const filterBySearchText = (alert: Alert): boolean => {
    const lowercaseSearchText = searchText.toLowerCase();
    return alert.description
      ? alert.description.toLowerCase().includes(lowercaseSearchText)
      : alert.trigger.toLowerCase().includes(lowercaseSearchText);
  };

  const allAlerts = alerts.filter(filterBySearchText);
  const mutedAlerts = allAlerts.filter((alert) => alert.silenced);
  const triggeredAlerts = allAlerts.filter((alert) => alert.lastStatus === AlertStatus.TRIGGERED);
  const incompleteAlerts = allAlerts.filter((alert) => alert.lastStatus === AlertStatus.INCOMPLETE);

  const hasSelectedAllMutedAlerts = mutedAlerts.length > 0 && mutedAlerts.every((alert) => (alert as Alert).selected);
  const hasSelectedAllTriggeredAlerts =
    triggeredAlerts.length > 0 && triggeredAlerts.every((alert) => (alert as Alert).selected);
  const hasSelectedAllIncompleteAlerts =
    incompleteAlerts.length > 0 && incompleteAlerts.every((alert) => (alert as Alert).selected);
  const hasSelectedAllAlerts = allAlerts.length > 0 && allAlerts.every((alert) => (alert as Alert).selected);
  const hasSelectedAtLeastOneAlert = alerts.some((alert) => alert.selected);
  const hasSelectedTemplatedAlerts = alerts.filter((alert) => alert.selected).some((alert) => isTemplatedAlert(alert));

  const isCurrentlyOnTriggeredTab = activeTab === TRIGGERED_ALERTS_TAB;
  const isCurrentlyOnMutedTab = activeTab === MUTED_ALERTS_TAB;
  const isCurrentlyOnAllTab = activeTab === ALL_ALERTS_TAB;
  const isCurrentlyOnIncompleteTab = activeTab === INCOMPLETE_ALERTS_TAB;

  const getSelectedAlerts = (): Array<Alert> => {
    if (isCurrentlyOnTriggeredTab) {
      return triggeredAlerts.filter((alert) => alert.selected);
    }

    if (isCurrentlyOnMutedTab) {
      return mutedAlerts.filter((alert) => alert.selected);
    }

    if (isCurrentlyOnIncompleteTab) {
      return incompleteAlerts.filter((alert) => alert.selected);
    }

    return allAlerts.filter((alert) => alert.selected);
  };

  const handleSelectAlert = (id: string): void => {
    const newAlerts = alerts.map((alert: Alert) => {
      if (alert.alertId === id) {
        alert.selected = !alert.selected;
      }
      return alert;
    });

    setAlerts(newAlerts);
  };

  const handleSelectAllMutedAlerts = (): void => {
    const newAlerts = alerts.map((alert: Alert) => {
      if (alert.silenced) {
        return {
          ...alert,
          selected: !hasSelectedAllMutedAlerts,
        };
      }

      return alert;
    });

    setAlerts(newAlerts);
  };

  const handleSelectAllTriggeredAlerts = (): void => {
    const newAlerts = alerts.map((alert: Alert) => {
      if (alert.lastStatus === AlertStatus.TRIGGERED) {
        return {
          ...alert,
          selected: !hasSelectedAllTriggeredAlerts,
        };
      }

      return alert;
    });

    setAlerts(newAlerts);
  };

  const handleSelectAllIncompleteAlerts = (): void => {
    const newAlerts = alerts.map((alert: Alert) => {
      if (alert.lastStatus === AlertStatus.INCOMPLETE) {
        return {
          ...alert,
          selected: !hasSelectedAllIncompleteAlerts,
        };
      }

      return alert;
    });

    setAlerts(newAlerts);
  };

  const handleSelectAllAlerts = (): void => {
    const newAlerts = alerts.map((alert: Alert) => ({
      ...alert,
      selected: !hasSelectedAllAlerts,
    }));

    setAlerts(newAlerts);
  };

  const unselectAlerts = (): void => {
    const newAlerts = alerts.map((alert: Alert) => ({
      ...alert,
      selected: false,
    }));
    setAlerts(newAlerts);
  };

  return {
    alertModel: {
      alerts: allAlerts,
      activeTab,
      searchText,
      mutedAlerts,
      triggeredAlerts,
      incompleteAlerts,
      hasSelectedAllAlerts,
      hasSelectedAllTriggeredAlerts,
      hasSelectedAllMutedAlerts,
      hasSelectedAllIncompleteAlerts,
      hasSelectedAtLeastOneAlert,
      hasSelectedTemplatedAlerts,
      setAlerts,
      setActiveTab,
      setSearchText,
      isCurrentlyOnTriggeredTab,
      isCurrentlyOnMutedTab,
      isCurrentlyOnIncompleteTab,
      isCurrentlyOnAllTab,
      handleSelectAllTriggeredAlerts,
      handleSelectAllMutedAlerts,
      handleSelectAllIncompleteAlerts,
      handleSelectAllAlerts,
      getSelectedAlerts,
      handleSelectAlert,
      unselectAlerts,
    },
    loading,
    error,
  };
}

export default useAlerts;
